import { Action } from "redux";

import { StrollerSearchFormData } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollerSearchForm";
import { FacetSetup } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import { FEATURES_LIST } from "@/mk2/apps/strollers/features";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";

///////////////////////////////////////////////////////////////////
// Strollers search - form - toggle more facets

export const STROLLERS_SEARCH_TOGGLE_MORE_FACETS = "STROLLERS_SEARCH_TOGGLE_MORE_FACETS";
export interface StrollersSearchToggleMoreFacetsAction extends Action {
    readonly type: typeof STROLLERS_SEARCH_TOGGLE_MORE_FACETS;
}
export const strollersSearchToggleMoreFacets = (): StrollersSearchToggleMoreFacetsAction => ({
    type: STROLLERS_SEARCH_TOGGLE_MORE_FACETS,
});

///////////////////////////////////////////////////////////////////
// Strollers search - form - on open Select facet value

export const STROLLERS_SEARCH_ON_OPEN_SELECT_FACET_VALUE =
    "STROLLERS_SEARCH_ON_OPEN_SELECT_FACET_VALUE";
export interface StrollersSearchOnOpenSelectFacetValueAction extends Action {
    readonly type: typeof STROLLERS_SEARCH_ON_OPEN_SELECT_FACET_VALUE;
    readonly facetSetup: FacetSetup;
}
export const strollersSearchOnOpenSelectFacetValue = (
    facetSetup: FacetSetup,
): StrollersSearchOnOpenSelectFacetValueAction => ({
    type: STROLLERS_SEARCH_ON_OPEN_SELECT_FACET_VALUE,
    facetSetup,
});

///////////////////////////////////////////////////////////////////
// Strollers search - form - submit

export const STROLLERS_SEARCH_SUBMIT_FORM = "STROLLERS_SEARCH_SUBMIT_FORM";
export interface StrollersSearchSubmitFormAction extends Action {
    readonly type: typeof STROLLERS_SEARCH_SUBMIT_FORM;
    readonly values: StrollerSearchFormData;
}
export const strollersSearchSubmitForm = (
    values: StrollerSearchFormData,
): StrollersSearchSubmitFormAction => ({
    type: STROLLERS_SEARCH_SUBMIT_FORM,
    values,
});

///////////////////////////////////////////////////////////////////
// Strollers search - fetch search results

export const STROLLERS_SEARCH_RESULTS_FETCH_TRIGGER = "STROLLERS_SEARCH_RESULTS_FETCH_TRIGGER";
export interface StrollersSearchResultsFetchTriggerAction {
    readonly type: typeof STROLLERS_SEARCH_RESULTS_FETCH_TRIGGER;
    readonly q: string;
    readonly offset: number;
}
export const STROLLERS_SEARCH_RESULTS_FETCH_REQUEST = "STROLLERS_SEARCH_RESULTS_FETCH_REQUEST";
export interface StrollersSearchResultsFetchRequestAction extends APIRequestAction {
    readonly type: typeof STROLLERS_SEARCH_RESULTS_FETCH_REQUEST;
    readonly q: string;
    readonly offset: number;
}
export interface StrollersSearchResultsFetchNormalizedResponse extends NormalizedResponse {
    readonly result: {
        strollers: number[];
        strollersPreviewPhotoMap: Record<number, number>;
        variants: number[];
        variantsPreviewPhotoMap: Record<number, number>;
        strollersPhotos: number[];
        brands: number[];
        buyingGuide: number;
    };
}
export const STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS = "STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS";
export interface StrollersSearchResultsFetchSuccessAction extends APISuccessAction<StrollersSearchResultsFetchNormalizedResponse> {
    readonly type: typeof STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS;
    readonly q: string;
    readonly offset: number;
    readonly hasMore: boolean;
    readonly count: number;

    readonly strollersCountPerBrand: Record<number, number>;
    readonly reviewsCountPerBrand: Record<number, number>;
    readonly brandsCount: number;
}

export const STROLLERS_SEARCH_RESULTS_FETCH_FAILURE = "STROLLERS_SEARCH_RESULTS_FETCH_FAILURE";
export interface StrollersSearchResultsFetchFailureAction extends APIFailureAction {
    readonly type: typeof STROLLERS_SEARCH_RESULTS_FETCH_FAILURE;
    readonly q: string;
    readonly offset: number;
}

export const strollersSearchResultsFetchTrigger = (
    q: string,
    offset?: number,
): StrollersSearchResultsFetchTriggerAction => ({
    type: STROLLERS_SEARCH_RESULTS_FETCH_TRIGGER,
    q,
    offset,
});
export const strollersSearchResultsFetchApi = {
    request: (q: string, offset: number): StrollersSearchResultsFetchRequestAction => ({
        type: STROLLERS_SEARCH_RESULTS_FETCH_REQUEST,
        q,
        offset,
    }),
    success: (
        q: string,
        offset: number,
        hasMore: boolean,
        count: number,

        strollersCountPerBrand: Record<number, number>,
        reviewsCountPerBrand: Record<number, number>,
        brandsCount: number,

        response: StrollersSearchResultsFetchNormalizedResponse,
    ): StrollersSearchResultsFetchSuccessAction => ({
        type: STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS,
        q,
        offset,
        hasMore,
        count,
        strollersCountPerBrand,
        reviewsCountPerBrand,
        brandsCount,
        response,
    }),
    failure: (q: string, offset: number, error: any): StrollersSearchResultsFetchFailureAction => ({
        type: STROLLERS_SEARCH_RESULTS_FETCH_FAILURE,
        q,
        offset,
        error,
    }),
};

export interface StrollersSearchLoadFacetsApiAction extends Action {
    readonly values: StrollerSearchFormData;
}

export interface SearchFacetsCounts {
    readonly type: Record<keyof typeof FEATURES_LIST.type.OPTIONS, number>;
    readonly type_2kombi: Record<keyof typeof FEATURES_LIST.type_2kombi.OPTIONS, number>;
    readonly brand_slug: Record<string, number>;
    readonly availability: Record<string, number>;
    readonly price: Record<string, number>;
    readonly color: Record<string, number>;
    readonly number_of_kids: Record<keyof typeof FEATURES_LIST.number_of_kids.OPTIONS, number>;
    readonly siblings_type: Record<keyof typeof FEATURES_LIST.siblings_type.OPTIONS, number>;
    readonly variant_rear_wheel_type: Record<string, number>;
    readonly rotating_fixation_wheels: Record<string, number>;
    readonly stroller_for_parent_by_height: Record<string, number>;
    readonly number_of_wheels_l: Record<string, number>;
    readonly rear_wheels_diameter: Record<string, number>;
    readonly recommended_age: Record<string, number>;
    readonly back_support_can_be_in_vertical_seat_position: Record<string, number>;
    readonly rotating_seat: Record<string, number>;
    readonly swinging: Record<string, number>;
    readonly spacious_carrycot: Record<string, number>;
    readonly spacious_seat: Record<string, number>;
    readonly weight_is_low: Record<string, number>;
    readonly very_good_folding: Record<string, number>;
    readonly big_shopping_basket: Record<string, number>;
    readonly manual_brake_present: Record<string, number>;
    readonly adjustable_handle: Record<string, number>;
    readonly foldable_handrail: Record<string, number>;
    readonly one_hand_folding: Record<string, number>;
    readonly foldable_carrycot: Record<string, number>;
    readonly carrycot_has_handle: Record<string, number>;
    readonly back_support_can_be_positioned: Record<string, number>;
    readonly leg_rest_can_be_positioned: Record<string, number>;
    readonly back_support_can_be_in_lying_position: Record<string, number>;
    readonly car_seat_can_be_attached: Record<string, number>;
    readonly supplied_pad: Record<string, number>;
    readonly washable_fabrics: Record<string, number>;
    readonly harness_type: Record<string, number>;
    readonly bumper_bar: Record<string, number>;
    readonly maximum_load_per_child_in_kg_range: Record<string, number>;
}

export const STROLLERS_SEARCH_LOAD_FACETS_API_REQUEST = "STROLLERS_SEARCH_LOAD_FACETS_API_REQUEST";

export interface StrollersSearchLoadFacetsApiRequestAction
    extends StrollersSearchLoadFacetsApiAction, APIRequestAction {
    readonly type: typeof STROLLERS_SEARCH_LOAD_FACETS_API_REQUEST;
}

export interface StrollersSearchLoadFacetsApiNormalizedResponse extends NormalizedResponse {
    readonly result: {};
}

export const STROLLERS_SEARCH_LOAD_FACETS_API_SUCCESS = "STROLLERS_SEARCH_LOAD_FACETS_API_SUCCESS";

export interface StrollersSearchLoadFacetsApiSuccessAction
    extends
        StrollersSearchLoadFacetsApiAction,
        APISuccessAction<StrollersSearchLoadFacetsApiNormalizedResponse> {
    readonly type: typeof STROLLERS_SEARCH_LOAD_FACETS_API_SUCCESS;
    readonly searchFacetsCounts: SearchFacetsCounts;
    readonly hits: number;
}

export const STROLLERS_SEARCH_LOAD_FACETS_API_FAILURE = "STROLLERS_SEARCH_LOAD_FACETS_API_FAILURE";

export interface StrollersSearchLoadFacetsApiFailureAction
    extends StrollersSearchLoadFacetsApiAction, APIFailureAction {
    readonly type: typeof STROLLERS_SEARCH_LOAD_FACETS_API_FAILURE;
}

export const strollersSearchLoadFacetsApi = {
    request: (values: StrollerSearchFormData): StrollersSearchLoadFacetsApiRequestAction => ({
        type: STROLLERS_SEARCH_LOAD_FACETS_API_REQUEST,
        values,
    }),
    success: (
        values: StrollerSearchFormData,
        searchFacetsCounts: SearchFacetsCounts,
        hits: number,
        response: StrollersSearchLoadFacetsApiNormalizedResponse,
    ): StrollersSearchLoadFacetsApiSuccessAction => ({
        type: STROLLERS_SEARCH_LOAD_FACETS_API_SUCCESS,
        values,
        hits,
        searchFacetsCounts,
        response,
    }),
    failure: (
        values: StrollerSearchFormData,
        error: any,
    ): StrollersSearchLoadFacetsApiFailureAction => ({
        type: STROLLERS_SEARCH_LOAD_FACETS_API_FAILURE,
        values,
        error,
    }),
};

export const STROLLERS_SEARCH_LOAD_FACETS = "STROLLERS_SEARCH_LOAD_FACETS";

export interface StrollersSearchLoadFacetsAction extends StrollersSearchLoadFacetsApiAction {
    readonly type: typeof STROLLERS_SEARCH_LOAD_FACETS;
}

export const strollersSearchLoadFacets = (
    values: StrollerSearchFormData,
): StrollersSearchLoadFacetsAction => ({
    type: STROLLERS_SEARCH_LOAD_FACETS,
    values,
});

export interface StrollersSearchLoadHelpApiAction extends Action {}

export const STROLLERS_SEARCH_LOAD_HELP_API_REQUEST = "STROLLERS_SEARCH_LOAD_HELP_API_REQUEST";

export interface StrollersSearchLoadHelpApiRequestAction
    extends StrollersSearchLoadHelpApiAction, APIRequestAction {
    readonly type: typeof STROLLERS_SEARCH_LOAD_HELP_API_REQUEST;
}

export interface StrollersSearchLoadHelpApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        readonly items: number[];
    };
}

export const STROLLERS_SEARCH_LOAD_HELP_API_SUCCESS = "STROLLERS_SEARCH_LOAD_HELP_API_SUCCESS";

export interface StrollersSearchLoadHelpApiSuccessAction
    extends
        StrollersSearchLoadHelpApiAction,
        APISuccessAction<StrollersSearchLoadHelpApiNormalizedResponse> {
    readonly type: typeof STROLLERS_SEARCH_LOAD_HELP_API_SUCCESS;
}

export const STROLLERS_SEARCH_LOAD_HELP_API_FAILURE = "STROLLERS_SEARCH_LOAD_HELP_API_FAILURE";

export interface StrollersSearchLoadHelpApiFailureAction
    extends StrollersSearchLoadHelpApiAction, APIFailureAction {
    readonly type: typeof STROLLERS_SEARCH_LOAD_HELP_API_FAILURE;
}

export const strollersSearchLoadHelpApi = {
    request: (): StrollersSearchLoadHelpApiRequestAction => ({
        type: STROLLERS_SEARCH_LOAD_HELP_API_REQUEST,
    }),
    success: (
        response: StrollersSearchLoadHelpApiNormalizedResponse,
    ): StrollersSearchLoadHelpApiSuccessAction => ({
        type: STROLLERS_SEARCH_LOAD_HELP_API_SUCCESS,
        response,
    }),
    failure: (error: any): StrollersSearchLoadHelpApiFailureAction => ({
        type: STROLLERS_SEARCH_LOAD_HELP_API_FAILURE,
        error,
    }),
};

export const STROLLERS_SEARCH_LOAD_HELP = "STROLLERS_SEARCH_LOAD_HELP";

export interface StrollersSearchLoadHelpAction extends Action {
    readonly type: typeof STROLLERS_SEARCH_LOAD_HELP;
}

export const strollersSearchLoadHelp = (): StrollersSearchLoadHelpAction => ({
    type: STROLLERS_SEARCH_LOAD_HELP,
});
